import {
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tag,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiUsers } from "react-icons/fi";
import {
  type MarketDialogueTeamDto,
  type MarketDialogueTeamPersonDto,
  type PersonDto,
  useAddMarketDialogueTeamMemberMutation,
  useGetMarketDialogueTeamQuery,
  useRemoveMarketDialogueTeamMemberMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { PersonSelector } from "../../common/persons/PersonSelector";

export const MarketDialogueTeamButton = ({ marketDialogueId }: { marketDialogueId: string }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, error } = useGetMarketDialogueTeamQuery({ marketDialogueId }, { skip: !isOpen });
  const displayer = useApiError();

  return (
    <>
      <Button size="sm" variant="outline" leftIcon={<Icon as={FiUsers} />} onClick={onOpen}>
        {t("Team")}
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Market dialogue team")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {error ? (
              displayer.show(error)
            ) : data ? (
              <TeamBody marketDialogueId={marketDialogueId} team={data} />
            ) : (
              <Text color="smMuted">{t("Loading")}...</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t("Close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const TeamBody = ({ marketDialogueId, team }: { marketDialogueId: string; team: MarketDialogueTeamDto }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const { selectedOrg, me } = useLoggedInWithOrgContextState();
  const [selected, setSelected] = useState<PersonDto | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [add] = useAddMarketDialogueTeamMemberMutation();
  const [adding, setAdding] = useState<string | null>(null);
  const [remove] = useRemoveMarketDialogueTeamMemberMutation();
  const reporter = team.needReporter;
  const onTeam = [team.responsible.personId, ...team.subjectExperts.map((member) => member.person.personId)];

  const addPerson = async (personId: string) => {
    setAdding(personId);
    try {
      await add({ marketDialogueId, addMarketDialogueTeamMemberRequest: { personId } }).unwrap();
      setSelected(null);
    } catch (e) {
      trigger(e);
    } finally {
      setAdding(null);
    }
  };

  const removePerson = async (personId: string) => {
    setRemoving(personId);
    try {
      await remove({ marketDialogueId, pathPersonId: personId }).unwrap();
    } catch (e) {
      trigger(e);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Stack spacing="4">
      <Text fontSize="sm" color="smMuted">
        {t("A subject expert can read every supplier answer and comment on it for the team, but cannot publish, invite suppliers or answer questions.")}
      </Text>
      <TeamRow person={team.responsible} role={t("Responsible buyer")} />
      {team.subjectExperts.map((member) => (
        <TeamRow
          key={member.person.personId}
          person={member.person}
          role={t("Subject expert")}
          onRemove={team.canChangeTeam ? () => void removePerson(member.person.personId) : undefined}
          isRemoving={removing === member.person.personId}
        />
      ))}
      {team.canChangeTeam && (
        <>
          <Divider />
          {reporter && (
            <Box borderWidth="1px" borderColor="smBorder" rounded="md" p="3">
              <Text fontSize="sm">
                {t("{{name}} reported the behov this market dialogue was started from.", { name: reporter.name })}
              </Text>
              <Button
                mt="2"
                size="sm"
                colorScheme="teal"
                onClick={() => void addPerson(reporter.personId)}
                isLoading={adding === reporter.personId}
              >
                {t("Add {{name}} as subject expert", { name: reporter.name })}
              </Button>
            </Box>
          )}
          <HStack align="flex-start">
            <Box flex="1">
              <PersonSelector
                size="md"
                organizationIds={[selectedOrg.id]}
                selectedPerson={selected}
                onChange={setSelected}
                personsToExclude={[...onTeam, me.id]}
                placeholder={t("Add a colleague as subject expert") ?? ""}
              />
            </Box>
            <Button
              colorScheme="teal"
              variant={reporter ? "outline" : "solid"}
              onClick={() => selected && void addPerson(selected.id)}
              isLoading={selected !== null && adding === selected.id}
              isDisabled={!selected}
            >
              {t("Add")}
            </Button>
          </HStack>
        </>
      )}
    </Stack>
  );
};

const TeamRow = ({
  person,
  role,
  onRemove,
  isRemoving = false,
}: {
  person: MarketDialogueTeamPersonDto;
  role: string;
  onRemove?: () => void;
  isRemoving?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <HStack justify="space-between" align="flex-start">
      <Stack spacing="0">
        <HStack>
          <Text fontWeight="600">{person.name}</Text>
          <Tag size="sm">{role}</Tag>
        </HStack>
        <Text fontSize="sm" color="smMuted">
          {person.email}
        </Text>
      </Stack>
      {onRemove && (
        <Button size="sm" variant="outline" colorScheme="red" onClick={onRemove} isLoading={isRemoving}>
          {t("Remove")}
        </Button>
      )}
    </HStack>
  );
};
